import type { UserNotificationResponseDto } from "../../types/types";
import { resolveNotificationPath } from "../../utils/NotificationUtils";

const APPLICANT_NOTIFICATION_TYPE: UserNotificationResponseDto["type"] = "GIG_REQUEST_NEEDS_REVIEW";

export type DashboardNotificationPreviewEntry = {
  id: string;
  title: string;
  body: string;
  ctaLabel: string;
  ctaPath: string;
  createdAt: string;
  needsAction: boolean;
  notifications: UserNotificationResponseDto[];
};

const parseNotificationUrl = (ctaPath: string) => {
  try {
    return new URL(ctaPath, "https://myteengig.local");
  } catch {
    return null;
  }
};

const getApplicantNotificationGigId = (notification: UserNotificationResponseDto) => {
  if (notification.type !== APPLICANT_NOTIFICATION_TYPE) {
    return null;
  }

  const url = parseNotificationUrl(notification.ctaPath);
  if (!url) {
    return null;
  }

  const pathGigId = url.pathname.match(/^\/adult\/gigs\/([^/]+)\/applicants$/)?.[1];
  return pathGigId ? decodeURIComponent(pathGigId) : url.searchParams.get("gigId");
};

const getApplicantNotificationGigTitle = (notification: UserNotificationResponseDto) => {
  const appliedForPhrase = " applied for ";
  const appliedForIndex = notification.body.indexOf(appliedForPhrase);
  if (appliedForIndex === -1) {
    return null;
  }

  const gigTitle = notification.body
    .slice(appliedForIndex + appliedForPhrase.length)
    .trim()
    .replace(/\.$/, "");

  return gigTitle.length > 0 ? gigTitle : null;
};

const getApplicantGroupPath = (notification: UserNotificationResponseDto) => {
  const url = parseNotificationUrl(notification.ctaPath);
  if (!url) {
    return resolveNotificationPath(notification);
  }

  const view = url.searchParams.get("view") ?? "NEEDS_APPROVAL";
  const pathGigId = url.pathname.match(/^\/adult\/gigs\/([^/]+)\/applicants$/)?.[1];
  if (pathGigId) {
    return `${url.pathname}?view=${encodeURIComponent(view)}`;
  }

  const queryGigId = url.searchParams.get("gigId");
  if (queryGigId) {
    return `/adult/gigs?view=${encodeURIComponent(view)}&gigId=${encodeURIComponent(queryGigId)}`;
  }

  return resolveNotificationPath(notification);
};

const buildSingleNotificationPreviewEntry = (
  notification: UserNotificationResponseDto,
): DashboardNotificationPreviewEntry => ({
  id: notification.id,
  title: notification.title,
  body: notification.body,
  ctaLabel: notification.ctaLabel,
  ctaPath: resolveNotificationPath(notification),
  createdAt: notification.createdAt,
  needsAction: notification.needsAction,
  notifications: [notification],
});

const buildApplicantNotificationPreviewEntry = (
  notification: UserNotificationResponseDto,
): DashboardNotificationPreviewEntry => ({
  id: `applicant-gig:${getApplicantNotificationGigId(notification)}`,
  title: notification.title,
  body: notification.body,
  ctaLabel: "Review Applicants",
  ctaPath: getApplicantGroupPath(notification),
  createdAt: notification.createdAt,
  needsAction: notification.needsAction,
  notifications: [notification],
});

const summarizeApplicantNotificationPreviewEntry = (
  entry: DashboardNotificationPreviewEntry,
): DashboardNotificationPreviewEntry => {
  const applicantCount = entry.notifications.length;
  if (applicantCount <= 1) {
    return entry;
  }

  const gigTitle = getApplicantNotificationGigTitle(entry.notifications[0]);
  return {
    ...entry,
    title: `${applicantCount} new applicants need review${gigTitle ? ` for ${gigTitle}` : ""}`,
    body: "Open the job applicant list to review these requests.",
  };
};

export const buildDashboardNotificationPreviewEntries = (
  notifications: UserNotificationResponseDto[],
): DashboardNotificationPreviewEntry[] => {
  const entries: DashboardNotificationPreviewEntry[] = [];
  const applicantEntriesByGigId = new Map<string, DashboardNotificationPreviewEntry>();

  notifications.forEach((notification) => {
    const applicantGigId = getApplicantNotificationGigId(notification);
    if (!applicantGigId) {
      entries.push(buildSingleNotificationPreviewEntry(notification));
      return;
    }

    const existingEntry = applicantEntriesByGigId.get(applicantGigId);
    if (existingEntry) {
      existingEntry.notifications.push(notification);
      return;
    }

    const entry = buildApplicantNotificationPreviewEntry(notification);
    applicantEntriesByGigId.set(applicantGigId, entry);
    entries.push(entry);
  });

  return entries.map(summarizeApplicantNotificationPreviewEntry);
};
